import { FaIcon, PopoverTag } from '@acp/core-ui';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { ComponentProps, useMemo } from 'react';
import { AccessIssueFacets } from '@acp/shared';
import { faCheckCircle } from '@fortawesome/sharp-light-svg-icons';

function AccessIssuesTagCollection({
    tags,
    ...props
}: {
    tags: string[];
} & ComponentProps<typeof VStack>) {
    const accessIssueFacetsArray: string[] = Object.values(
        AccessIssueFacets
    ).filter((item) => item !== AccessIssueFacets.NotOverdue);
    const hasAccessIssueTags = useMemo(
        () => tags.some((s) => accessIssueFacetsArray.includes(s)),
        [tags]
    );

    return (
        <VStack alignItems="flex-start" gap={2} wrap="wrap" {...props}>
            {!hasAccessIssueTags ? (
                <HStack>
                    <FaIcon icon={faCheckCircle} color="muted.500" />
                    <Text color="muted.500" fontSize="sm">
                        No compliance issues
                    </Text>
                </HStack>
            ) : (
                <>
                    {tags.includes(AccessIssueFacets.Overdue) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="danger"
                            title="Overdue"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Overdue
                            </Text>
                            <Text fontSize="small">
                                One or more services are overdue
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.Vacant) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="blackAlpha"
                            title="Property vacant"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Property vacant
                            </Text>
                            <Text fontSize="small">
                                Our records indicate that this property is
                                currently vacant
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.OnHold) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="blackAlpha"
                            title="Services on hold"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Services on hold
                            </Text>
                            <Text fontSize="small">
                                One or more services have been placed on hold
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.PriorityBooking) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="blackAlpha"
                            title="Priority booking"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Priority booking
                            </Text>
                            <Text fontSize="small">
                                We are attempting to prioritise one or more
                                priority services
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.UpdateRequired) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="blackAlpha"
                            title="Update required"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Update required
                            </Text>
                            <Text fontSize="small">
                                Occupants information of this property is requiring to be updated
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.SuspensionWarning) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="orange"
                            title="Suspension warning"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Suspension warning
                            </Text>
                            <Text fontSize="small">
                                This property is in suspension warning due to important issues
                                that have remained unresolved for more than 21 days
                            </Text>
                        </PopoverTag>
                    )}
                    {tags.includes(AccessIssueFacets.Suspended) && (
                        <PopoverTag
                            popoverPlacement="auto"
                            colorScheme="danger"
                            title="Suspended"
                        >
                            <Text fontSize="small" fontWeight="bold">
                                Suspended
                            </Text>
                            <Text fontSize="small">
                                This property is under suspension as issues remain unresolved
                            </Text>
                        </PopoverTag>
                    )}
                </>
            )}
        </VStack>
    );
}

export { AccessIssuesTagCollection };
